/**
 * Leaderboard Page
 * Grade-level weekly leaderboard with anonymous handles, XP ranking, and current child highlight
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Zap, Crown, RefreshCw, ArrowLeft, Medal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useChildStore } from '../hooks';
import { getLeaderboard } from '../services/leaderboardService';
import { GRADE_LEVEL_NAMES } from '@kids-lms/constants';
import type { LeaderboardEntry } from '@kids-lms/types';

const PERIOD_LABELS = { weekly: 'This Week', alltime: 'All Time' };

const rankColors = [
  'from-yellow-400 to-amber-500',   // 1st
  'from-slate-300 to-slate-400',    // 2nd
  'from-amber-600 to-amber-700',    // 3rd
];

const rankIcons = [
  <Crown className="w-5 h-5 text-yellow-600" />,
  <Medal className="w-5 h-5 text-slate-500" />,
  <Medal className="w-5 h-5 text-amber-700" />,
];

function XpBar({ pct }: { pct: number }) {
  return (
    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden w-16">
      <motion.div
        className="h-full bg-gradient-to-r from-kid-ocean to-kid-violet rounded-full"
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      />
    </div>
  );
}

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const { selectedChild } = useChildStore();
  const [period, setPeriod] = useState<'weekly' | 'alltime'>('weekly');
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [currentChild, setCurrentChild] = useState<LeaderboardEntry | null>(null);
  const [gradeLevel, setGradeLevel] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const maxXp = entries[0]?.weeklyXp || 1;

  useEffect(() => {
    if (!selectedChild) return;
    setLoading(true);
    setError('');
    getLeaderboard(selectedChild.id, period)
      .then((data) => {
        setEntries(data.entries);
        setCurrentChild(data.currentChild);
        setGradeLevel(data.gradeLevel);
      })
      .catch(() => setError('Could not load leaderboard'))
      .finally(() => setLoading(false));
  }, [selectedChild, period]);

  if (!selectedChild) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400">No child selected</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kid-bg pb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-kid-violet to-kid-berry px-6 pt-10 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          {[...Array(20)].map((_, i) => (
            <Star key={i} className="absolute text-white" style={{ left: `${(i * 37) % 100}%`, top: `${(i * 53) % 100}%`, width: 8 + (i % 4) * 4 }} />
          ))}
        </div>
        <div className="relative max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/learn')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-6 text-sm font-kid"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-kid-sm flex items-center justify-center backdrop-blur-sm">
              <Trophy className="w-8 h-8 text-yellow-300" />
            </div>
            <div>
              <h1 className="text-3xl font-display text-white">Leaderboard</h1>
              <p className="text-white/80 text-kid-sm font-kid">
                {GRADE_LEVEL_NAMES[gradeLevel] || `Grade ${gradeLevel}`} · Top Learners
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-8">
        {/* Period Toggle */}
        <div className="bg-white rounded-kid shadow-kid-lg border-2 border-kid-border p-1.5 flex gap-1 mb-6">
          {(['weekly', 'alltime'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`flex-1 py-2 rounded-kid-sm text-sm font-display transition-all ${
                period === p
                  ? 'bg-gradient-to-r from-kid-violet to-kid-berry text-white shadow-kid'
                  : 'text-kid-text-soft hover:text-kid-text'
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>

        {/* Your Rank Banner */}
        {currentChild && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-kid-dandelion/20 to-kid-tangerine/10 border-2 border-kid-dandelion rounded-kid p-4 mb-6 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-kid-dandelion/30 rounded-full flex items-center justify-center font-display text-kid-tangerine text-lg">
                #{currentChild.rank}
              </div>
              <div>
                <p className="font-display text-kid-text text-sm">Your Rank</p>
                <p className="text-kid-text-soft text-kid-xs font-kid">{currentChild.handle}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-display text-kid-tangerine text-lg">{currentChild.weeklyXp} <span className="text-xs">XP</span></p>
              <p className="text-kid-xs text-kid-text-soft font-kid">Level {currentChild.level}</p>
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-kid-violet animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-gray-400 font-kid">{error}</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16">
            <Trophy className="w-16 h-16 mx-auto text-gray-200 mb-4" />
            <h3 className="font-display text-kid-text text-xl mb-2">Be the First!</h3>
            <p className="text-kid-text-soft font-kid text-sm max-w-xs mx-auto">
              No one in your grade has earned XP yet this week. Start learning to take the #1 spot!
            </p>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={period}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-3"
            >
              {/* Top 3 podium */}
              {entries.length >= 3 && (
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[entries[1], entries[0], entries[2]].map((entry, podiumIdx) => {
                    const actualRank = podiumIdx === 0 ? 2 : podiumIdx === 1 ? 1 : 3;
                    const heights = ['h-24', 'h-32', 'h-20'];
                    const podiumH = heights[podiumIdx];
                    return (
                      <motion.div
                        key={entry.handle}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: podiumIdx * 0.1 }}
                        className={`flex flex-col items-center ${podiumIdx === 1 ? 'order-2' : podiumIdx === 0 ? 'order-1' : 'order-3'}`}
                      >
                        <div className={`text-3xl mb-1 ${entry.isCurrentChild ? 'animate-bounce' : ''}`}>
                          {actualRank === 1 ? '🥇' : actualRank === 2 ? '🥈' : '🥉'}
                        </div>
                        <div className="text-center mb-2">
                          <p className={`font-display text-kid-text text-xs ${entry.isCurrentChild ? 'text-kid-violet font-bold' : ''}`}>
                            {entry.handle}
                          </p>
                          <p className="text-kid-xs text-kid-text-soft font-kid">{entry.weeklyXp} XP</p>
                        </div>
                        <div className={`w-full ${podiumH} rounded-t-kid bg-gradient-to-t ${rankColors[actualRank - 1]} flex items-center justify-center`}>
                          <span className="text-white font-display text-xl">#{actualRank}</span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Full list */}
              {entries.map((entry, i) => (
                <motion.div
                  key={entry.handle}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className={`bg-white rounded-kid border-2 p-4 flex items-center gap-4 shadow-kid ${
                    entry.isCurrentChild
                      ? 'border-kid-violet ring-2 ring-kid-violet/20'
                      : 'border-kid-border'
                  }`}
                >
                  {/* Rank */}
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    i < 3 ? `bg-gradient-to-br ${rankColors[i]}` : 'bg-gray-100'
                  }`}>
                    {i < 3
                      ? rankIcons[i]
                      : <span className="text-sm font-bold text-gray-500">#{entry.rank}</span>
                    }
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`font-display text-sm truncate ${entry.isCurrentChild ? 'text-kid-violet' : 'text-kid-text'}`}>
                        {entry.handle}
                        {entry.isCurrentChild && <span className="ml-1 text-kid-xs text-kid-violet font-kid"> (You!)</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <XpBar pct={(entry.weeklyXp / maxXp) * 100} />
                      <span className="text-kid-xs text-kid-text-soft font-kid">Lv.{entry.level}</span>
                    </div>
                  </div>

                  {/* XP */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Zap className="w-3.5 h-3.5 text-kid-dandelion" />
                    <span className="font-display text-sm text-kid-text">{entry.weeklyXp}</span>
                    <span className="text-kid-xs text-kid-text-soft">XP</span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        )}

        {/* Motivation */}
        <div className="mt-8 bg-gradient-to-r from-kid-ocean to-kid-violet rounded-kid p-5 text-white text-center">
          <p className="font-display text-lg mb-1">Every question earns XP! 🚀</p>
          <p className="text-white/80 text-kid-sm font-kid">
            Chat, complete habits, and finish challenges to climb the rankings!
          </p>
        </div>
      </div>
    </div>
  );
}
